import { withTheme } from '@emotion/react';
import classNames from 'classnames';
import React from 'react';
import compose from 'utils/compose';

import PropTypes from 'prop-types';
import { withStyles } from 'tss-react/mui';

import ArrowIcon from '@mui/icons-material/KeyboardArrowRight';
import Divider from '@mui/material/Divider/index';
import IconButton from '@mui/material/IconButton/index';
import ListItem from '@mui/material/ListItem/index';
import Modal from '@mui/material/Modal';
import Popper from '@mui/material/Popper/index';
import Typography from '@mui/material/Typography';

import { ensureArray } from 'app-components/utils/core/immutableHelpers';

import ListItemIcon from '@mui/material/ListItemIcon';
import QTip from 'components/QuickenControls/QTip';
import { noop } from 'lodash';

/*
 *   - 'options' for the menu should be passed down as an array of objects, like the following
 *   {
 *     label: <string>,            // this is what's displayed in the menu
 *     value: <any>,               // this is what's returned when an option is selected
 *
 *     ***-OPTIONAL PROPERTIES-***
 *     subMenu: <array of options> // will render a subMenu under this list item
 *     id: <string>                // will give the menu option a specified id
 *     customRender: <node>        // will be displayed inside the list item instead of regular Typography wrapping
 *     isSubheader: <bool>         // if true shows the option as a subheader and restricts selecting it
 *     onSelect: <func>            // overrides regular onSelect functionality (which regularly returns 'value')
 *     divider: <bool>             // adds a list divider after this option
 *     nop: <bool>                 // nop === no-operation, this option will not display in the menu
 *     backgroundColor: <string>   // overrides list item bg color, separate from 'style' to ensure hover colors display properly
 *     style: <object>             // overrides styling on the list item, to override styling on the text use 'customRender'
 *                                     note: do NOT add backgroundColor in here, it will mess up the hover effects
 *     startIcon:                  // icon that goes to left of option text
 *     endIcon:                   // icon that goes to right of option text
 *     subtext:                    // text displayed under the main option
 *     tooltip: <string>           // If you wish to show a tooltip when hovering over the item
 *     saveMenuSelectionOnTab: <bool>           // Performs a "save" (onChange) when tabbing
 *
 *   }
 *
 *   - currently doesn't support groupLists as options
 *   - keyDown events are caught by the menu for navigation
 *
 */

const styles = (theme) => ({
  divider: {
    margin: `${theme.shape.padding[1]}px 0px`,
  },
  item: {
    cursor: 'pointer',
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius * 1,
    ':not(.keyboard-mode) &': {
      '&:hover': {
        backgroundColor: `${theme.palette.action.hover} !important`,
      },
    },
  },
  disabledItem: {
    color: theme.palette.text.disabled,
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius * 1,
  },
  flexRow: {
    alignItems: 'center',
    display: 'flex',
  },
  popper: {
    zIndex: 1500, // just needs to be >1300 to display on top of Modal
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[16],
    maxHeight: '95%',
  },
  primaryMenu: {
    maxHeight: '100%',
    overflowY: 'auto',
    padding: theme.shape.padding[2],
  },
  root: {
    zIndex: 1500,
    // needs to be >=1500 to display backdrop on top of Modal (1300) and Drawer (1500)
    // or else backdrop click wont close the menu
  },
  rootPopper: {
    minWidth: 280,
    maxWidth: '50%',
  },
  secondaryMenu: {
    maxHeight: 'calc(95vh)',
    overflowY: 'auto',
    borderRadius: theme.shape.borderRadius * 2,
    overflowX: 'hidden',
  },
  subheader: {
    color: `${theme.palette.text.secondary}`,
    textAlign: 'left',
    padding: theme.shape.padding[2],
  },
  startIcon: {
    alignItems: 'center',
    minWidth: 30,
    marginRight: theme.spacing(1),
  },
  iconColor: {
    color: theme.palette.icon.main,
  },
  iconColorDisabled: {
    color: theme.palette.text.disabled,
  },
  rightArrowStyles: {
    padding: 0,
    width: 20,
    height: 20,
  },
  rightArrowStylesDisabled: {
    padding: 0,
    width: 20,
    height: 20,
    color: theme.palette.text.disabled,
  },
  rightArrowButton: {
    padding: 0,
    width: 30,
    height: 30,
  },
  labelStyles: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    marginLeft: 5,
    paddingTop: 5,
    paddingBottom: 5,
  },
});

class NestedQMenu extends React.PureComponent {
  constructor(props) {
    super(props);
    this.scrollRefInternal = [React.createRef()];
    this.trackedOptions = [];
    this.state = {
      // the lists match up indecision to the depth of the menu
      path: [],
      subNodes: [],
      interactionMode: 'keyboard',
      headerHeight: 0,
    };
    this.composeItems(props.options, 0);
  }

  componentDidMount() {
    this.initMenu('first');
    if (this.props.open) {
      document.addEventListener('keydown', this.handleKeyPress, { capture: true });
      document.addEventListener('mousemove', this.handleMouseMove);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.options !== this.props.options) {
      this.initMenu('first');
    }

    if (!prevProps.open && this.props.open) {
      document.addEventListener('keydown', this.handleKeyPress, { capture: true });
      document.addEventListener('mousemove', this.handleMouseMove);
    } else if (prevProps.open && !this.props.open) {
      document.removeEventListener('keydown', this.handleKeyPress, { capture: true });
      document.removeEventListener('mousemove', this.handleMouseMove);
      this.clearAllTracking();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress, { capture: true });
    document.removeEventListener('mousemove', this.handleMouseMove);
    this.closeMenus();
  }

  scrollRef = () => this.scrollRefInternal[this.state.path.length];

  handleMouseMove = () => {
    if (this.state.interactionMode !== 'mouse') {
      this.setState({ interactionMode: 'mouse' });
    }
  };

  openSubMenu = (option, selectFirst = false) => {
    let { path, subNodes } = this.state;
    const { menuDepth } = option;

    // update the path to the currently hovered item
    path = path.slice(0, menuDepth).concat([option]);

    // trim subnodes to existing depth
    subNodes = subNodes.slice(0, menuDepth);
    // add subnodes for current layer
    subNodes = option.subMenu ? subNodes.concat([ensureArray(option.subMenu)]) : subNodes;

    if (selectFirst) {
      // auto-highlights the first available option in submenu
      const nextDepth = path.length;
      const nextIndex = 0;
      const nextOption = ensureArray(option.subMenu)[nextIndex];
      if (nextOption) {
        const optionToSelect = {
          ...nextOption,
          id: this.getOptionId(nextOption, nextDepth, nextIndex),
          menuDepth: nextDepth,
          menuIndex: nextIndex,
        };
        path = path.concat([optionToSelect]);
      }
    }

    this.setState({ path, subNodes });
  };

  handleOptionHover = (_event, option) => {
    let { interactionMode } = this.state;

    // ignore hover events if in keyboard mode
    if (interactionMode === 'keyboard') return;

    this.openSubMenu(option);
  };

  doOptionSelect = (event, option) => {
    event.stopPropagation();
    if (option.onSelect) {
      option.onSelect(event);
      this.openSubMenu(option);
      return false;
    } else {
      if (this.props.onChange && option.value) {
        this.props.onChange(option.value);
      }
      if (this.props.currentHover) {
        this.props.currentHover(null);
      }
      return true;
    }
  };

  handleOptionClick = (event, option) => {
    this.setState({ interactionMode: 'mouse' });

    // this is really meant for testing - unit & cypress; otherwise, handleOptionHover already ran
    const currentOption = this.state.path[this.getCurrentDepth()];
    if (!currentOption || option?.id !== currentOption?.id) {
      this.handleOptionHover(event, option);
    }

    this.doOptionSelect(event, option);
  };

  // sends a 'fake' event to key press because logic is already there
  handleArrowClick = (event) => {
    this.handleKeyPress({
      // key: 'ArrowRight',
      keyCode: 39,
      stopPropagation: () => 0,
      preventDefault: () => 0,
    });
    event.stopPropagation();
  };

  /**
   * Handles key press events for keyboard navigation.
   * Predicts submenu id, avoids accessing trackedOptions during prediction,
   * and sets path/subNodes only as needed.
   */
  handleKeyPress = (event) => {
    // If menu is closed or has no options, ignore event
    if (this.props.options.length === 0 || !this.props.open) {
      return;
    }

    let { path, subNodes } = this.state;
    let currentDepth = this.getCurrentDepth();

    if (this.state.interactionMode !== 'keyboard') {
      // When we switch to keyboard mode and there are more subNodes displayed vs selected path (which can
      // happen if mouse was on parent-node, child submenu was displayed, and then we move to keyboard mode),
      // then remove displayed but not yet focused submenu.
      //
      subNodes = subNodes.slice(0, currentDepth);
      this.setState({
        interactionMode: 'keyboard',
        subNodes,
      });
    }

    const currentOption = path[currentDepth];

    // Handle Enter and Tab (selection)
    if (event.key === 'Enter' || (this.props.saveMenuSelectionOnTab && event.key === 'Tab')) {
      if (!currentOption) return;
      if (!this.props.tabOnEnter) {
        event.stopPropagation();
        event.preventDefault();
      }
      // select on enter
      if (this.doOptionSelect(event, currentOption)) {
        this.closeMenus();
      }
      return;
    }

    // Handle Escape (close menu)
    if (event.key === 'Tab' || event.key === 'Escape') {
      this.closeMenus(event);
      return;
    }

    // If nothing is selected, handle initial navigation
    if (!currentOption) {
      if (event.key === 'ArrowDown') {
        this.initMenu('first');
      } else if (event.key === 'ArrowUp') {
        if (this.scrollRef()) {
          this.scrollRef().scrollTop = this.scrollRef().scrollHeight;
        }
        this.initMenu('last');
      }
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    // Prevent default for all arrow keys
    if (['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Handle ArrowRight (open submenu)
    if (event.key === 'ArrowRight' && currentOption.subMenu) {
      this.openSubMenu(currentOption, true);
      return;
    }

    // Handle ArrowLeft (close submenu)
    if (event.key === 'ArrowLeft') {
      if (path.length > 1) {
        path = path.slice(0, -1);
        subNodes = subNodes.slice(0, -1);
        this.setState({ path, subNodes });
      }
      return;
    }

    // Handle ArrowDown (move to next item) - updated: do NOT auto-open submenu
    if (event.key === 'ArrowDown') {
      const nextOption = this.getNextOption(this.state.path[currentDepth]);
      const { id, menuIndex } = nextOption;

      const scroller = this.scrollRefInternal[currentDepth];
      const child = document.getElementById(id);

      if (scroller && child) {
        if (menuIndex === 0) {
          // wrapped to first
          scroller.scrollTop = 0;
        } else if (child.offsetTop + 2 * child.offsetHeight >= scroller.offsetHeight + scroller.scrollTop) {
          scroller.scrollTop += child.offsetHeight;
        }
      }

      const path = this.state.path.slice(0, -1).concat([nextOption]);
      this.setState({ path });

      return;
    }

    // Handle ArrowUp (move to previous item) - updated: do NOT auto-open submenu
    if (event.key === 'ArrowUp') {
      const prevOption = this.getPrevOption(this.state.path[currentDepth]);
      const { id, menuIndex, menuDepth } = prevOption;

      const scroller = this.scrollRefInternal[currentDepth];
      const child = document.getElementById(id);

      if (scroller && child) {
        if (menuIndex === this.trackedOptions[menuDepth].length - 1) {
          // wrapped to last
          scroller.scrollTop = scroller.scrollHeight;
        } else if (child.offsetTop - child.offsetHeight <= scroller.scrollTop) {
          scroller.scrollTop -= child.offsetHeight;
        }
      }

      const path = this.state.path.slice(0, -1).concat([prevOption]);
      this.setState({ path });
      return;
    }
  };

  findOption = (options, option, path = []) => {
    let pathFound;

    options.forEach((item) => {
      if (item.value === option.value) {
        pathFound = [...path, item];
      } else if (item.subMenu && item.subMenu.length) {
        const subResult = this.findOption(item.subMenu, option, [...path, item]);
        if (subResult) {
          pathFound = subResult;
        }
      }
    });

    return pathFound;
  };

  initMenu = (firstOrLast, offset = 0) => {
    if (!this.props.open) return;
    let path;
    if (this.props.selectedOption) {
      path = this.findOption(this.trackedOptions[0], this.props.selectedOption);
    } else if (firstOrLast === 'first') {
      const first = this.trackedOptions[0][offset];
      if (first) {
        path = [first];
        setTimeout(() => {
          const scroll = this.scrollRef();
          if (scroll && scroll.scrollTop) {
            scroll.scrollTop = 0;
          }
        }, 0);
      }
    } else if (firstOrLast === 'last') {
      const last = this.trackedOptions[0][this.trackedOptions[0].length - 1 + offset];
      path = last ? [last] : undefined;
    }
    if (!path || !path.length) return;

    const option = path[this.getCurrentDepth()];
    this.setState({
      path,
      subNodes: option && option.subMenu ? [ensureArray(option.subMenu)] : [],
    });
  };

  getCurrentDepth = () => this.state.path.length - 1;

  getOptionId = (option, depth, index) => option.id ?? `${index}${option.label}${depth}`;
  // getOptionId = (option, depth, index) => option.id ?? `${depth}_${index}_${option.label}`;

  getNextOption = (currentOption) => {
    if (currentOption.menuIndex >= this.trackedOptions[currentOption.menuDepth].length - 1) {
      return this.trackedOptions[currentOption.menuDepth][0]; // wraps back around to the beginning
    }
    return this.trackedOptions[currentOption.menuDepth][currentOption.menuIndex + 1];
  };

  getPrevOption = (currentOption) => {
    if (currentOption.menuIndex <= 0) {
      return this.trackedOptions[currentOption.menuDepth][this.trackedOptions[currentOption.menuDepth].length - 1]; // wrap around to end
    }
    return this.trackedOptions[currentOption.menuDepth][currentOption.menuIndex - 1];
  };

  pathContainsOption = (path, option) =>
    path.some(
      (crumb) =>
        crumb.label === option.label && option.menuDepth === crumb.menuDepth && crumb.menuIndex === option.menuIndex,
    );

  composeItems = (options, depth = 0) => {
    if (!options) return [];
    if (this.trackedOptions.length < depth + 1) this.trackedOptions.push([]);
    else this.trackedOptions[depth] = [];
    const items = [];
    const { path } = this.state;
    options.forEach((option, index) => {
      if (!option) {
        return;
      }
      if (option.isLabel) {
        items.push(
          <Typography
            id={this.getOptionId(option, depth, index)}
            variant='captionRegular'
            className={this.props.classes.labelStyles}
            key={`label-${option.label}`}
          >
            {option.label}
          </Typography>,
        );
      } else if (option.isSubheader) {
        items.push(
          <ListItem
            id={this.getOptionId(option, depth, index)}
            key={`subtitle-${option.label}`}
            style={{ backgroundColor: option.backgroundColor || '' }}
            className={this.props.classes.subheader}
          >
            {option.startIcon && (
              <ListItemIcon className={this.props.classes.startIcon}>{option.startIcon}</ListItemIcon>
            )}
            <Typography variant='captionRegular'>{option.label}</Typography>
            {option.endIcon && <ListItemIcon className={this.props.classes.startIcon}>{option.endIcon}</ListItemIcon>}
          </ListItem>,
        );
      } else if (option.isGroupList) {
        // TODO: add group lists into the menu
      } else if (!option.nop) {
        // regular items and ones with submenus
        const trackedOption = {
          ...option,
          menuDepth: depth,
          menuIndex: this.trackedOptions[depth].length,
          id: this.getOptionId(option, depth, index),
        };
        this.trackedOptions[depth].push(trackedOption);
        const onClick = option.disabled ? noop : (event) => this.handleOptionClick(event, trackedOption, depth);
        const itemToPush = (
          <ListItem
            id={trackedOption.id}
            // eslint-disable-next-line react/no-array-index-key
            key={`qnested-menuitem-${index}${option.label}${depth}`}
            onMouseEnter={(event) => this.handleOptionHover(event, trackedOption)}
            disabled={option.disabled}
            onClick={onClick}
            className={classNames(
              !option.disabled && this.props.classes.item,
              option.disabled && this.props.classes.disabledItem,
              this.state.interactionMode === 'keyboard' && 'keyboard-mode',
            )}
            role='menuitem'
            style={{
              backgroundColor:
                !option.disabled &&
                (!this.pathContainsOption(path, trackedOption)
                  ? option.backgroundColor || ''
                  : this.props.theme.palette.action.hover),
              ...(option.style ? option.style : {}),
            }}
            // selected={this.pathContainsOption(path, trackedOption)}
          >
            {option.customRender ? (
              option.customRender
            ) : (
              <div className={this.props.classes.flexRow}>
                {option.startIcon && (
                  <ListItemIcon
                    className={option.disabled ? this.props.classes.iconColorDisabled : this.props.classes.iconColor}
                    classes={{ root: this.props.classes.startIcon }}
                  >
                    {option.startIcon}
                  </ListItemIcon>
                )}
                <div>
                  <Typography variant={this.props.typographyVariant} color={option.labelColor}>
                    {option.label}
                  </Typography>
                  {option.subtext && <Typography variant='captionRegular'>{option.subtext}</Typography>}
                </div>
                {option.endIcon && (
                  <ListItemIcon
                    className={option.disabled ? this.props.classes.iconColorDisabled : this.props.classes.iconColor}
                    classes={{ root: this.props.classes.startIcon }}
                  >
                    {option.endIcon}
                  </ListItemIcon>
                )}
              </div>
            )}
            {option.subMenu && (
              <IconButton onClick={this.handleArrowClick} className={this.props.classes.rightArrowButton} size='large'>
                <ArrowIcon
                  className={
                    option.disabled ? this.props.classes.rightArrowStylesDisabled : this.props.classes.rightArrowStyles
                  }
                />
              </IconButton>
            )}
          </ListItem>
        );

        if (option.tooltip) {
          items.push(
            <QTip
              placement={option.tooltip.placement}
              solidTheme
              title={option.tooltip.text}
              noTippyTip
              arrow
              enableOnMobile
              key={option.tooltip.text}
            >
              {itemToPush}
            </QTip>,
          );
        } else {
          items.push(itemToPush);
        }
      }

      if (option.divider) {
        items.push(
          <Divider
            // eslint-disable-next-line react/no-array-index-key
            key={`${index}divider${depth}`}
            className={this.props.classes.divider}
          />,
        );
      }
    });
    return items;
  };

  getAnchor = () => this.props.anchorEl || null;

  closeMenus = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (this.props.onClose && this.props.open) {
      this.props.onClose();
    }
    this.clearAllTracking();
    if (this.props.currentHover) {
      this.props.currentHover(null);
    }

    // restore focus to element which opened menu
    if (this.props.anchorEl && typeof this.props.anchorEl.focus === 'function') {
      this.props.anchorEl.focus();
    }
  };

  setHeaderRef = (ref) => {
    if (ref) {
      const headerHeight = ref.getBoundingClientRect().bottom - ref.getBoundingClientRect().top;
      this.setState({ headerHeight });
    }
  };

  clearAllTracking = () =>
    this.setState({
      path: [],
      subNodes: [],
      interactionMode: 'keyboard',
    });

  render() {
    const { anchorEl, options, classes, open, header, width, placement } = this.props;
    const { subNodes, headerHeight } = this.state;
    if (!anchorEl || !options) return null;

    const spaceBelow = window.innerHeight - anchorEl.getBoundingClientRect().top - 40; // 40 for spacing
    const spaceAbove = anchorEl.getBoundingClientRect().top - 40; // 40 for spacing
    const rootPlacement = spaceAbove > spaceBelow ? 'top' : 'bottom';
    const rootHeight = (spaceAbove > spaceBelow ? spaceAbove : spaceBelow) - headerHeight;

    const menuItems = this.composeItems(options, 0);

    // callback to let parent know if the user is navigating through the menu
    if (this.props.currentHover) {
      if (this.state.path.length > 0 && open) {
        this.props.currentHover(this.state.path[this.getCurrentDepth()]);
      } else {
        this.props.currentHover(null);
      }
    }

    return (
      <Modal
        className={classes.root}
        open={Boolean(open)}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            event.stopPropagation();
            this.closeMenus();
          }
        }}
        BackdropProps={{ invisible: true }}
      >
        <>
          <Popper
            id='picker-boi'
            data-testid='picker-boi'
            key='picker-boi'
            open
            anchorEl={this.getAnchor}
            onClick={(event) => {
              event?.stopPropagation();
              event?.preventDefault();
            }}
            placement={placement || rootPlacement}
            className={menuItems.length > 0 ? classNames(classes.popper, classes.rootPopper) : ''}
            style={{
              // maxHeight: rootHeight,
              width: width || anchorEl.offsetWidth,
            }}
            modifiers={[
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['right', 'left', 'bottom', 'top'],
                },
              },
            ]}
          >
            <div key='headerDiv==' ref={this.setHeaderRef}>
              {header}
            </div>
            <div
              id='top-wrapper-qnested-menu'
              className={classes.primaryMenu}
              style={{ maxHeight: rootHeight - 40 }} //
              ref={(node) => {
                if (node) {
                  this.scrollRefInternal[0] = node;
                }
              }}
            >
              {menuItems.map((item) => item)}
            </div>
          </Popper>
          {open &&
            this.state.path.map((option, index) => {
              if (!subNodes[index]) return;
              const { label } = option;
              const popperId = `qnested-popper-menu-item-${label}`;
              const id = `qnested-menu-item-${label}`;
              return (
                <Popper
                  id={popperId}
                  key={popperId}
                  data-testid={popperId}
                  open
                  anchorEl={document.getElementById(option.id)}
                  className={classes.popper}
                  placement='right'
                >
                  <div
                    id={id}
                    className={classes.secondaryMenu}
                    ref={(node) => {
                      if (node) {
                        // +1 to accommodate for root-menu
                        this.scrollRefInternal[index + 1] = node;
                      }
                    }}
                  >
                    {this.composeItems(subNodes[index], index + 1).map((item) => item)}
                  </div>
                </Popper>
              );
            })}
        </>
      </Modal>
    );
  }
}

NestedQMenu.propTypes = {
  onClose: PropTypes.func,
  anchorEl: PropTypes.object,
  classes: PropTypes.object,
  onChange: PropTypes.func,
  options: PropTypes.array,
  selectedOption: PropTypes.object,
  open: PropTypes.bool,
  header: PropTypes.object,
  theme: PropTypes.object,
  currentHover: PropTypes.func, // callback that passes the option currently being hovered, has some extra key-values, just ignore
  width: PropTypes.number,
  tabOnEnter: PropTypes.bool,
  placement: PropTypes.string,
  saveMenuSelectionOnTab: PropTypes.bool,
  typographyVariant: PropTypes.string,
};

export default compose(withTheme)(withStyles(NestedQMenu, styles));
